/**
 * Helpers de UI para la página de uso (CLI 28).
 *
 * AP-11: tier público (`Rápido`/`Equilibrado`/`Profundo`) y feature
 * legible. El backend almacena valores internos (`fast`/`balanced`/`deep`
 * en `tier`; `analysis_turn`/`case_record_summary`/etc. en `feature`).
 * La UI los traduce sin exponer providers ni model IDs privados.
 */

export const TIER_LABELS: Record<string, string> = {
  fast: "Rápido",
  balanced: "Equilibrado",
  deep: "Profundo",
};

export const FEATURE_LABELS: Record<string, string> = {
  analysis_turn: "Análisis interactivo",
  case_record_summary: "Resumen de caso",
  report_recommendations: "Recomendaciones de reporte",
  proactive_diagnostic: "Diagnóstico proactivo",
  ecc_nova: "ECC · área NOVA",
  unspecified: "Sin etiquetar",
};

export function tierLabel(tier: string): string {
  return TIER_LABELS[tier] ?? tier;
}

export function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? feature;
}

export function formatNumber(n: number): string {
  return n.toLocaleString("es-MX");
}

export function formatUsd(n: number): string {
  return n.toLocaleString("es-MX", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4,
  });
}

export function rangePreset(value: string): { since?: string; until?: string } {
  const now = new Date();
  if (value === "last_7_days") {
    const since = new Date(now);
    since.setUTCDate(since.getUTCDate() - 7);
    return { since: since.toISOString().slice(0, 10), until: now.toISOString().slice(0, 10) };
  }
  if (value === "last_30_days") {
    const since = new Date(now);
    since.setUTCDate(since.getUTCDate() - 30);
    return { since: since.toISOString().slice(0, 10), until: now.toISOString().slice(0, 10) };
  }
  return {};
}

/**
 * Proyecta un consumo del mes en curso a fin de mes (regla de tres sobre
 * los días transcurridos). Devuelve null si no hay nada que proyectar.
 *
 * Se usa para la marca de proyección de los medidores de cuota: ir al 40%
 * el día 3 rumbo a 400% no se lee igual que ir al 40% el día 28.
 */
export function projectToMonthEnd(used: number, now: Date = new Date()): number | null {
  if (used <= 0) return null;
  const diasDelMes = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0),
  ).getUTCDate();
  // Fracción del mes ya corrida. Piso de medio día para no proyectar al
  // infinito con el primer consumo del día 1.
  const transcurridos = Math.max(
    0.5,
    now.getUTCDate() - 1 + now.getUTCHours() / 24 + now.getUTCMinutes() / 1440,
  );
  return (used * diasDelMes) / transcurridos;
}

/** Fecha corta en español, para etiquetas de reinicio de cuota. */
export function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
  });
}

/** Moneda de reporte. El original queda visible para poder cuadrar factura. */
export function formatMxn(n: number): string {
  return n.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  });
}

/** Fecha y hora local, para el detalle llamada por llamada. */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Bytes en unidades de proveedor: 1 kB = 1000 B, no 1024.
 *
 * La cuota y la factura del proveedor están en TB decimales. Usar 1024 aquí
 * pintaría un 9 % menos de consumo que el que se va a cobrar — un error
 * silencioso justo en el número que hay que defender.
 */
export function formatBytes(n: number | null): string {
  if (n === null) return "—";
  const unidades = ["B", "kB", "MB", "GB", "TB", "PB"];
  let valor = Math.abs(n);
  let i = 0;
  while (valor >= 1000 && i < unidades.length - 1) {
    valor /= 1000;
    i += 1;
  }
  const decimales = valor >= 100 || i === 0 ? 0 : valor >= 10 ? 1 : 2;
  return `${(n < 0 ? -valor : valor).toFixed(decimales)} ${unidades[i]}`;
}

/** Importe en la moneda que reporte el proveedor (euros, normalmente). */
export function formatMoneda(n: number | null, moneda: string | null): string {
  if (n === null) return "—";
  if (!moneda) return n.toFixed(2);
  return n.toLocaleString("es-MX", {
    style: "currency",
    currency: moneda,
    maximumFractionDigits: 2,
  });
}
